"use client"

import { ArrowRight, Check } from "lucide-react"
import Link from "next/link"
import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  useTransition,
} from "react"

import { BrickReferenceImageInput } from "@/components/brick/BrickReferenceImageInput"
import { CONTACT_GENRES, CONTACT_SERVICES } from "@/data/contact"
import { getAnalyticsMode } from "@/lib/analytics-mode"
import {
  type BrickReferenceImage,
  brickReferenceImageFile,
} from "@/lib/brick-reference-image"
import type {
  ContactPurpose,
  ContactResult,
  ContactServiceId,
} from "@/lib/contact"
import { createContactFunnel } from "@/lib/contact-analytics"

import { submitContact } from "./actions"
import { type ChallengeFailure, ContactChallenge } from "./ContactChallenge"
import styles from "./page.module.css"

const subscribeToHydration = () => () => undefined

type ContextFields = Partial<
  Record<"from" | "example" | "photo" | "inquiry" | "brickEstimate", string>
>

export function ContactForm({
  purpose = "consultation",
  materialOptions = [],
  initialService,
  initialTopic = "business",
  contextFields = {},
  referenceText,
  ready,
  siteKey,
}: {
  purpose?: ContactPurpose
  materialOptions?: { id: ContactServiceId; name: string }[]
  initialService?: ContactServiceId
  initialTopic?: string
  contextFields?: ContextFields
  referenceText?: string
  ready: boolean
  siteKey?: string
}) {
  const materials = purpose === "materials"
  const [genre, setGenre] = useState<string>(
    initialService ?? (initialTopic !== "business" ? initialTopic : ""),
  )
  const retainReference = genre === initialService
  // JavaScriptが動かない場合に、通常のHTML送信へ流れないようにする。
  const hydrated = useSyncExternalStore(
    subscribeToHydration,
    () => true,
    () => false,
  )
  const [funnel] = useState(() => createContactFunnel(purpose, initialService))
  const [referenceImage, setReferenceImage] =
    useState<BrickReferenceImage | null>(null)
  const [referenceImageBusy, setReferenceImageBusy] = useState(false)
  const imageProcessing = useRef(false)
  function changeImageBusy(busy: boolean) {
    // eslint-disable-next-line functional/immutable-data -- 同じイベントで画像処理中の送信を防ぐ
    imageProcessing.current = busy
    setReferenceImageBusy(busy)
  }
  const [clientErrors, setClientErrors] = useState<
    Record<string, string | undefined>
  >({})
  const [result, setResult] = useState<ContactResult | null>(null)
  const [pending, startTransition] = useTransition()
  const [token, setToken] = useState("")
  const challengeHadFailure = useRef(false)
  const challengeNeedsRefresh = useRef(false)
  const [challengeVersion, setChallengeVersion] = useState(0)
  const [verificationRequested, setVerificationRequested] = useState(false)
  const form = useRef<HTMLFormElement>(null)
  const waitingForChallenge = useRef(false)
  const resumingSubmission = useRef(false)
  const challengeFeedback = useRef<HTMLDivElement>(null)
  const submitting = useRef(false)
  const feedback = useRef<HTMLDivElement>(null)
  const errors = {
    ...(result?.status === "error" ? result.errors : undefined),
    ...clientErrors,
  }

  useEffect(() => {
    if (result) feedback.current?.focus()
  }, [result])

  const resetChallenge = () => {
    // eslint-disable-next-line functional/immutable-data -- 再生成した試行では期限切れの送信時更新を重ねない
    challengeNeedsRefresh.current = false
    setToken("")
    setChallengeVersion((value) => value + 1)
  }

  const cancelVerification = useCallback(() => {
    // eslint-disable-next-line functional/immutable-data -- 利用者の送信待機を取り消す
    waitingForChallenge.current = false
    setVerificationRequested(false)
  }, [])
  const challengeFailed = useCallback(
    (reason: ChallengeFailure, code?: string) => {
      cancelVerification()
      // eslint-disable-next-line functional/immutable-data -- 再生成後も認証の復帰を一度だけ記録する
      challengeHadFailure.current = true
      funnel.challengeError(reason, code)
    },
    [cancelVerification, funnel],
  )

  const receiveChallengeToken = useCallback(
    (nextToken: string) => {
      setToken(nextToken)
      if (nextToken) {
        // eslint-disable-next-line functional/immutable-data -- 手動更新や幅変更による新しい認証も採用する
        challengeNeedsRefresh.current = false
      }
      if (nextToken && challengeHadFailure.current) {
        // eslint-disable-next-line functional/immutable-data -- 同じ認証復帰の重複計測を防ぐ
        challengeHadFailure.current = false
        funnel.challengeRecovered()
      }
    },
    [funnel],
  )
  const challengeExpired = useCallback(() => {
    // eslint-disable-next-line functional/immutable-data -- 期限切れは失敗ではなく次の送信操作で更新する
    challengeNeedsRefresh.current = true
  }, [])

  useEffect(() => {
    funnel.view(ready)
    const onPageHide = () => {
      cancelVerification()
      funnel.abandon()
    }
    window.addEventListener("pagehide", onPageHide)
    return () => {
      window.removeEventListener("pagehide", onPageHide)
      // eslint-disable-next-line functional/immutable-data -- 画面遷移後に保留した送信を再開しない
      waitingForChallenge.current = false
      funnel.abandon()
    }
  }, [funnel, ready, cancelVerification])

  useEffect(() => {
    if (!token || !waitingForChallenge.current || referenceImageBusy) return
    // eslint-disable-next-line functional/immutable-data -- 明示された送信を認証完了後に一度だけ再開する
    waitingForChallenge.current = false
    // eslint-disable-next-line functional/immutable-data -- 自動再開は追加の送信操作として計測しない
    resumingSubmission.current = true
    setVerificationRequested(false)
    form.current?.requestSubmit()
    // eslint-disable-next-line functional/immutable-data -- ネイティブ入力検証で送信が止まった場合も解除する
    resumingSubmission.current = false
  }, [token, referenceImageBusy])

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting.current || !hydrated || !ready || imageProcessing.current)
      return
    if (!resumingSubmission.current) funnel.attempt()
    if (!token) {
      if (!waitingForChallenge.current) funnel.challengeWait()
      // eslint-disable-next-line functional/immutable-data -- 認証待ち中の送信意思を保持する
      waitingForChallenge.current = true
      if (challengeNeedsRefresh.current) resetChallenge()
      setVerificationRequested(true)
      challengeFeedback.current?.focus()
      return
    }
    cancelVerification()
    setClientErrors({})
    const data = new FormData(event.currentTarget)
    if (referenceImage) {
      try {
        data.set("referenceImage", brickReferenceImageFile(referenceImage))
      } catch {
        setResult({
          status: "error",
          errorType: "validation",
          message: "参考画像を確認してください。入力内容は保持されています。",
          errors: {
            referenceImage: "画像を選び直すか、削除して送信してください。",
          },
        })
        return
      }
    }
    if (getAnalyticsMode() !== "external") data.set("internal_test", "yes")
    // eslint-disable-next-line functional/immutable-data -- 同じイベントループ内の二重送信を防ぐref
    submitting.current = true
    startTransition(async () => {
      try {
        const response = await submitContact(data)
        setResult(response)
        if (response.status === "success") {
          setReferenceImage(null)
          funnel.success(
            CONTACT_SERVICES.some(({ id }) => id === genre) ? 1 : 0,
          )
        } else {
          funnel.error(response.errorType)
          resetChallenge()
        }
      } catch {
        funnel.error("network")
        setResult({
          errorType: "network",
          status: "error",
          message:
            "通信を確認できませんでした。入力内容は保持されています。もう一度送信してください。",
        })
        resetChallenge()
      } finally {
        // eslint-disable-next-line functional/immutable-data -- 送信終了後にrefのロックを解除
        submitting.current = false
      }
    })
  }

  if (result?.status === "success")
    return (
      <section
        className={styles.success}
        ref={feedback}
        tabIndex={-1}
        role="status"
      >
        <Check size={28} aria-hidden />
        <h2>
          {materials ? "資料請求を受け付けました" : "ご相談を受け付けました"}
        </h2>
        <p>
          {materials
            ? "ご希望の資料をこちらからご覧いただけます。"
            : "担当者が内容を確認し、ご入力のメールアドレスにご連絡します。"}
        </p>
        <p>
          {result.receiptEmail === "sent"
            ? materials
              ? "資料のリンクをメールでもお送りしました。"
              : "受付確認メールをお送りしました。"
            : materials
              ? "資料のメール送付ができませんでした。こちらのリンクからご覧ください。"
              : "受付は完了していますが、受付確認メールを送れませんでした。"}
        </p>
        {materials ? (
          <ul className={styles.downloads}>
            {result.downloads.map((item) => (
              <li key={item.id}>
                <a href={item.url} target="_blank" rel="noopener noreferrer">
                  {item.name}を開く ↗
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <Link href="/materials">サービス資料を請求する →</Link>
        )}
      </section>
    )

  return (
    <form
      ref={form}
      id="contact-form"
      method="post"
      className={styles.form}
      aria-busy={pending || referenceImageBusy}
      onSubmit={onSubmit}
      onChangeCapture={(event) => {
        const target = event.target
        if (
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement
        ) {
          funnel.change(target.name)
          if (
            (target.name === "email" || target.name === "consent") &&
            target.validity.valid
          )
            setClientErrors((current) => ({
              ...current,
              [target.name]: undefined,
            }))
        }
      }}
      onInvalidCapture={(event) => {
        cancelVerification()
        const target = event.target
        if (
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement
        ) {
          funnel.invalid(target.name)
          if (target.name === "email" || target.name === "consent")
            setClientErrors((current) => ({
              ...current,
              [target.name]:
                target.name === "email"
                  ? "メールアドレスの形式をご確認ください（例：name@example.com）。"
                  : "個人情報の取り扱いをご確認のうえ、同意にチェックしてください。",
            }))
        }
      }}
    >
      <div className={styles.formHead}>
        <h2>
          {materials
            ? "ご希望の資料をお選びください"
            : "相談したいことを、お聞かせください"}
        </h2>
        <p>
          {materials
            ? "ご請求後、資料をその場で開けます。メールでもお送りします。"
            : "法人・団体からのご相談を中心に承っています。個人の方もご相談いただけます。"}
        </p>
      </div>
      {referenceText && retainReference && !materials && (
        <details className={styles.optionalFields} open>
          <summary>このご相談に添える情報</summary>
          <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {referenceText}
          </p>
        </details>
      )}
      {!ready && (
        <p className={styles.notice} role="status">
          現在、フォームの受付を準備しています。お急ぎのご相談は
          <a
            href="https://line.me/R/ti/p/@217onxfu"
            target="_blank"
            rel="noopener noreferrer"
          >
            LINE公式アカウント
          </a>
          へお寄せください。
        </p>
      )}
      {result?.status === "error" && (
        <div
          className={styles.errorSummary}
          ref={feedback}
          tabIndex={-1}
          role="alert"
        >
          <p>{result.message}</p>
          <ul>
            {Object.entries(errors)
              .filter(([, message]) => !!message)
              .map(([field, message]) => (
                <li key={field}>
                  <a href={`#${field}`}>{message}</a>
                </li>
              ))}
          </ul>
        </div>
      )}
      <fieldset disabled={pending} className={styles.fields}>
        <legend className="sr-only">ご相談フォーム</legend>
        <input type="hidden" name="purpose" value={purpose} />
        {Object.entries(contextFields)
          .filter(
            ([name]) => name === "from" || (retainReference && !materials),
          )
          .map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        <input type="hidden" name="cf-turnstile-response" value={token} />
        <div className={styles.honeypot} aria-hidden="true">
          <label htmlFor="website">ウェブサイト（入力不要）</label>
          <input id="website" name="website" autoComplete="off" tabIndex={-1} />
        </div>
        <div className={styles.field}>
          <label htmlFor="genre">
            {materials ? "ご希望の資料" : "お問い合わせジャンル"}{" "}
            <span className={styles.required}>必須</span>
          </label>
          <select
            id="genre"
            name="genre"
            required
            value={genre}
            onChange={(event) => setGenre(event.currentTarget.value)}
            aria-invalid={!!errors.genre}
            aria-describedby={errors.genre ? "genre-error" : undefined}
          >
            <option value="">選択してください</option>
            {(materials ? materialOptions : CONTACT_GENRES).map(
              ({ id, name }) => (
                <option key={id} value={id}>
                  {id === "other" ? "その他・未定" : name}
                </option>
              ),
            )}
          </select>
          {errors.genre && (
            <p id="genre-error" className={styles.fieldError}>
              {errors.genre}
            </p>
          )}
        </div>
        <div className={styles.field}>
          <label htmlFor="message">
            ご相談内容{" "}
            <span className={materials ? styles.optional : styles.required}>
              {materials ? "任意" : "必須"}
            </span>
          </label>
          <textarea
            id="message"
            name="message"
            required={!materials}
            maxLength={3000}
            rows={5}
            placeholder="つくりたいものや、実現したいことをお聞かせください。"
            aria-invalid={!!errors.message}
            aria-describedby={
              errors.message ? "message-help message-error" : "message-help"
            }
          />
          <p id="message-help" className={styles.help}>
            用途・必要な仕様・数量・希望時期・予算など、決まっていることがあれば一緒にご記入ください。未定の項目は空欄で大丈夫です。
          </p>
          {errors.message && (
            <p id="message-error" className={styles.fieldError}>
              {errors.message}
            </p>
          )}
        </div>
        {!materials && (
          <BrickReferenceImageInput
            value={referenceImage}
            onChange={(image) => {
              setReferenceImage(image)
              if (image) funnel.change("referenceImage")
            }}
            disabled={pending}
            error={errors.referenceImage}
            onBusyChange={changeImageBusy}
          />
        )}
        <div className={styles.field}>
          <label htmlFor="name">
            お名前 <span className={styles.required}>必須</span>
          </label>
          <input
            id="name"
            name="name"
            required
            maxLength={100}
            autoComplete="name"
            placeholder="山田 太郎"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "name-error" : undefined}
          />
          {errors.name && (
            <p id="name-error" className={styles.fieldError}>
              {errors.name}
            </p>
          )}
        </div>
        <div className={styles.field}>
          <label htmlFor="email">
            メールアドレス <span className={styles.required}>必須</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            maxLength={254}
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            inputMode="email"
            placeholder="your-name@example.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "email-error" : "email-help"}
            onBlur={(event) => {
              if (
                event.currentTarget.value &&
                !event.currentTarget.validity.valid
              )
                setClientErrors((current) => ({
                  ...current,
                  email:
                    "メールアドレスの形式をご確認ください（例：name@example.com）。",
                }))
            }}
          />
          <p id="email-help" className={styles.help}>
            受付確認と担当者の返信をお送りします。
          </p>
          {errors.email && (
            <p id="email-error" className={styles.fieldError}>
              {errors.email}
            </p>
          )}
        </div>
        <div className={styles.field}>
          <label htmlFor="company">
            会社・団体名 <span className={styles.required}>必須</span>
          </label>
          <input
            id="company"
            name="company"
            required
            maxLength={200}
            autoComplete="organization"
            aria-invalid={!!errors.company}
            aria-describedby={
              errors.company ? "company-help company-error" : "company-help"
            }
          />
          <p id="company-help" className={styles.help}>
            個人の方は個人名をご入力ください。
          </p>
          {errors.company && (
            <p id="company-error" className={styles.fieldError}>
              {errors.company}
            </p>
          )}
        </div>
        <label className={styles.consent}>
          <input
            type="checkbox"
            id="consent"
            name="consent"
            required
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? "consent-error" : undefined}
          />
          <span>
            個人情報の取り扱いに同意する{" "}
            <span className={styles.required}>必須</span>
            <small className={styles.consentPolicy}>
              <Link href="/privacy" target="_blank" rel="noopener noreferrer">
                プライバシーポリシーを確認する（別タブ）
              </Link>
            </small>
          </span>
        </label>
        {errors.consent && (
          <p id="consent-error" className={styles.fieldError}>
            {errors.consent}
          </p>
        )}
        {ready && siteKey && (
          <div
            className={styles.challenge}
            ref={challengeFeedback}
            tabIndex={-1}
            aria-label="送信前の確認"
          >
            {verificationRequested && !token && (
              <div role="status">
                <p>
                  まだ送信されていません。下の確認が完了すると、この内容で送信します。入力内容は保持されています。
                </p>
                <button
                  type="button"
                  className={styles.textLink}
                  onClick={cancelVerification}
                >
                  送信待ちを取り消す
                </button>
              </div>
            )}
            <ContactChallenge
              key={challengeVersion}
              siteKey={siteKey}
              onToken={receiveChallengeToken}
              onFailure={challengeFailed}
              onExpired={challengeExpired}
            />
            <button
              type="button"
              className={styles.textLink}
              onClick={resetChallenge}
            >
              確認欄を再読み込み
            </button>
          </div>
        )}
        <button
          type="submit"
          className={styles.submit}
          disabled={!hydrated || !ready || pending || referenceImageBusy}
          aria-describedby={ready ? "contact-challenge-status" : undefined}
        >
          {pending
            ? "送信しています…"
            : materials
              ? "資料を請求する"
              : "相談内容を送信する"}
          <ArrowRight size={19} aria-hidden />
        </button>
        <p className={styles.help}>
          {materials
            ? "ご相談内容の記入は任意です。資料だけでもご請求いただけます。"
            : genre === "yoshinani-brick"
              ? "通常2営業日以内を目安に、担当者からご返信します。内容によりお時間をいただく場合があります。"
              : "担当者より、ご入力のメールアドレスにご連絡します。"}
        </p>
        <noscript>
          <p>フォームの送信にはJavaScriptを有効にしてください。</p>
        </noscript>
      </fieldset>
    </form>
  )
}
